import { useState, useRef, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@state/hooks'
import { parseImageMarkers } from '@utils/imageMarkerParser'
import {
  addMessageToConversation,
  updateConversationTitle,
  updateAnalysisReport,
  updateWorkflowState,
  updateStrategyWorkspace,
} from '@state/slices/conversationsSlice'
import { setGraphContext } from '@state/slices/graphSlice'
import { omapApi } from '@services/omapApi'
import { getNumericCompanyId } from '@utils/companyUtils'
import { useIsSampleCompany } from '@state/hooks/useIsSampleCompany'
import { isRefusedResponse, REFUSAL_FALLBACK_MESSAGE } from '@utils/refusal'
import { showRateLimitToast, showErrorToast } from '../../utils/toastApi'
import type { ChatMessage, ResponseMode, PlanningPath } from '@models/index'
import MarkdownWithTabLinks from './MarkdownWithTabLinks'
import { ProgressModal } from '@components/shared/ProgressModal'
import './ChatPanel.css'

function ChatPanel() {
  const dispatch = useAppDispatch()
  const { activeConversationId, conversations } = useAppSelector(state => state.conversations)
  const { activeCompanyId } = useAppSelector(state => state.companies)
  const isSampleCompany = useIsSampleCompany()
  const companyId = getNumericCompanyId(activeCompanyId)
  const activeConversation = conversations.find(c => c.id === activeConversationId)

  const [input, setInput] = useState('')
  const [selectedMode, setSelectedMode] = useState<ResponseMode>('quick')
  const [planningPath, setPlanningPath] = useState<PlanningPath>('guided')
  const [loading, setLoading] = useState(false)
  const [progressModal, setProgressModal] = useState<{
    visible: boolean
    mode: 'Quick' | 'Strategy Planning'
    completed: boolean
  }>({ visible: false, mode: 'Quick', completed: false })
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Orientation state (Plan 11)
  const TIER_THRESHOLDS = { TIER1: 60 * 60 * 1000, TIER2: 4 * 60 * 60 * 1000, TIER3: 12 * 60 * 60 * 1000 }
  const [lastViewedAt, setLastViewedAt] = useState<Record<string, number>>({})
  const [orientationMessage, setOrientationMessage] = useState<{
    topic: string; substance: string; status: string
  } | null>(null)
  const [orientationTier, setOrientationTier] = useState<1 | 2 | 3 | null>(null)
  const [refreshingOrientation, setRefreshingOrientation] = useState(false)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [activeConversation?.messages])

  // Orientation tier logic (Plan 11)
  useEffect(() => {
    if (!activeConversationId || !activeConversation) {
      setOrientationMessage(null)
      setOrientationTier(null)
      return
    }

    const orientation = activeConversation.workflowState?.conversation_orientation
    if (!orientation || !orientation.topic) {
      setOrientationMessage(null)
      setOrientationTier(null)
      setLastViewedAt(prev => ({ ...prev, [activeConversationId]: Date.now() }))
      return
    }

    const lastViewed = lastViewedAt[activeConversationId]
    const elapsed = lastViewed ? Date.now() - lastViewed : Infinity

    let tier: 1 | 2 | 3 | null = null
    if (elapsed >= TIER_THRESHOLDS.TIER3) {
      tier = 3
    } else if (elapsed >= TIER_THRESHOLDS.TIER2) {
      tier = 2
    } else if (elapsed >= TIER_THRESHOLDS.TIER1) {
      tier = 1
    }

    if (tier) {
      setOrientationMessage(orientation)
      setOrientationTier(tier)
    } else {
      setOrientationMessage(null)
      setOrientationTier(null)
    }

    setLastViewedAt(prev => ({ ...prev, [activeConversationId]: Date.now() }))
  }, [activeConversationId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefreshOrientation = async () => {
    if (!activeConversationId || refreshingOrientation) return
    setRefreshingOrientation(true)
    try {
      const numericId = parseInt(activeConversationId.replace('conv_', ''))
      const data = await omapApi.refreshOrientation(numericId)
      if (data.orientation) {
        setOrientationMessage(data.orientation)
        setOrientationTier(3)
      }
    } catch (err) {
      console.error('[Orientation] Refresh failed:', err)
    } finally {
      setRefreshingOrientation(false)
    }
  }

  // Phase 6: if the active conversation has an in-flight async_job, resume polling.
  // Triggers when activeConversationId changes (mount or conversation switch).
  useEffect(() => {
    if (!activeConversationId) return
    const convIdStr = activeConversationId
    const convIdNum = typeof convIdStr === 'string'
      ? parseInt(convIdStr.replace('conv_', ''))
      : convIdStr

    let cancelled = false

    ;(async () => {
      try {
        const progress = await omapApi.getStrategyProgress(convIdNum)
        const job = (progress as any).async_job
        if (cancelled) return
        if (job && (job.status === 'pending' || job.status === 'running')) {
          setProgressModal({ visible: true, mode: 'Strategy Planning', completed: false })
          try {
            const workflowResponse = await omapApi.pollStrategyJob(convIdNum)
            if (cancelled) return
            // Hydrate the same way the inline Express success branch does
            dispatch(updateStrategyWorkspace({
              conversationId: convIdStr,
              data: {
                meceTable: (workflowResponse as any).mece_table || {},
                strategyMap: (workflowResponse as any).strategy_map,
                comprehensiveGate: null,
              },
            }))
            dispatch(updateAnalysisReport({
              conversationId: convIdStr,
              data: {
                coverageAttestation: (workflowResponse as any).coverage_attestation
                  || (workflowResponse as any).coverage_report?.coverage_attestation || '',
                strategicSynthesis: (workflowResponse as any).strategic_synthesis || null,
                strategicObjectives: (workflowResponse as any).strategic_objectives || [],
                fullReport: (workflowResponse as any).strategy_plan_document || '',
              },
            }))
            setProgressModal({ visible: false, mode: 'Strategy Planning', completed: true })
          } catch (err: any) {
            if (!cancelled) {
              const status = (err as any)?.status ?? (err as any)?.response?.status
              if (status === 429) {
                const retryAfter = Number(
                  (err as any)?.response?.headers?.['retry-after'] ??
                  (err as any)?.retryAfter ?? 0,
                )
                showRateLimitToast(retryAfter || undefined)
                setProgressModal({ visible: false, mode: 'Strategy Planning', completed: false })
                return
              }
              if ((err as any)?.name === 'PollingNetworkError') {
                showErrorToast(
                  'We lost the connection while your analysis was running. It may still be finishing, so please check back in a moment before retrying.',
                )
                setProgressModal({ visible: false, mode: 'Strategy Planning', completed: false })
                return
              }
              setProgressModal({ visible: false, mode: 'Strategy Planning', completed: false })
              const errorMsg: ChatMessage = {
                id: `msg_${Date.now()}`,
                role: 'assistant',
                content: `**The automatic analysis didn't finish**\n\nThis sometimes happens if the request was very broad or the service was briefly busy. You can:\n\n- **Try again** (most retries succeed).\n- Switch to **Guided** mode to build the strategy step by step.\n\n_(Technical detail: ${err?.errorCode || err?.message || 'no error code returned'})_`,
                timestamp: new Date().toISOString(),
              }
              dispatch(addMessageToConversation({
                conversationId: convIdStr,
                message: errorMsg,
              }))
            }
          }
        }
      } catch {
        // No-op: probably no conversation row or progress endpoint failed; user sees normal UI.
      }
    })()

    return () => {
      cancelled = true
    }
  }, [activeConversationId, dispatch])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!input.trim() || !activeConversationId || loading) return

    const userMessage: ChatMessage = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString(),
    }

    // Check if this is the first message BEFORE adding it
    const isFirstMessage = activeConversation?.messages.length === 0

    // Add user message to conversation
    dispatch(addMessageToConversation({
      conversationId: activeConversationId,
      message: userMessage,
    }))

    // Update conversation title immediately if this is the first message
    if (isFirstMessage) {
      const title = userMessage.content.length > 50
        ? userMessage.content.substring(0, 50) + '...'
        : userMessage.content
      dispatch(updateConversationTitle({
        conversationId: activeConversationId,
        title,
      }))
    }

    setInput('')
    setLoading(true)

    try {
      // Call appropriate API based on selected mode
      let response: any

      if (selectedMode === 'quick') {
        // Check if we're in dev mode and backend is unavailable
        const isDev = import.meta.env.VITE_DEV_MODE === 'true'

        // Show progress modal
        setProgressModal({ visible: true, mode: 'Quick', completed: false })

        try {
          response = await omapApi.submitQuickQuery(userMessage.content, activeConversationId, undefined, companyId)

          // Signal completion
          setProgressModal({ visible: true, mode: 'Quick', completed: true })
          setTimeout(() => setProgressModal({ visible: false, mode: 'Quick', completed: false }), 500)
        } catch (error: any) {
          // Hide modal on error
          setProgressModal({ visible: false, mode: 'Quick', completed: false })

          // If backend unavailable in dev mode, use mock response
          if (isDev && (error.code === 'ERR_NETWORK' || error.message.includes('Network Error'))) {
            console.log('[DEV MODE] Backend unavailable, using mock response')
            response = {
              response: `**Quick Mode Response (Mock)**\n\nYou asked: "${userMessage.content}"\n\nThis is a mock response since the Causeway backend is not running. In production, this would be a strategic analysis from the Neo4j knowledge graph.\n\n**Key Points:**\n- Strategic factor analysis\n- Evidence-based recommendations\n- Citations to knowledge graph nodes\n\n*Start the Causeway backend to see real responses.*`,
              conversation_id: activeConversationId,
            }
          } else {
            throw error
          }
        }

        // Refusal short-circuit: scope classifier blocked the prompt (Plan 2D-a).
        // Render refusal text directly in chat bubble and skip Analysis Report / graph updates.
        if (isRefusedResponse(response)) {
          const refusalMessage: ChatMessage = {
            id: `msg_${Date.now() + 1}`,
            role: 'assistant',
            content: response.response || REFUSAL_FALLBACK_MESSAGE,
            timestamp: new Date().toISOString(),
          }
          dispatch(addMessageToConversation({
            conversationId: activeConversationId,
            message: refusalMessage,
          }))
          return
        }

        const chatSummary = response.chat_summary
        const assistantMessage: ChatMessage = {
          id: `msg_${Date.now() + 1}`,
          role: 'assistant',
          content: chatSummary
            ? `${chatSummary}\n\n[See full analysis in the Analysis Report tab.](#analysis-report)`
            : `I've analyzed your question. **[See full analysis in the Analysis Report tab.](#analysis-report)**`,
          timestamp: new Date().toISOString(),
        }

        dispatch(addMessageToConversation({
          conversationId: activeConversationId,
          message: assistantMessage,
        }))

        // Add to Analysis Report (Quick Insights section)
        const currentReport = activeConversation?.analysisReportData || {}
        const quickResponses = currentReport.quickResponses || []

        dispatch(updateAnalysisReport({
          conversationId: activeConversationId,
          data: {
            ...currentReport,
            quickResponses: [
              ...quickResponses,
              {
                question: userMessage.content,
                answer: response.response || 'No response received',
                timestamp: new Date().toISOString(),
              },
            ],
          },
        }))

        // Update graph context for Business Graph Tab (Jan 2026)
        if (response.graph_context) {
          dispatch(setGraphContext(response.graph_context))

          // Also update workspace to persist graph context
          dispatch(updateStrategyWorkspace({
            conversationId: activeConversationId,
            data: {
              graphContext: response.graph_context,
            }
          }))
        }

      } else if (selectedMode === 'strategy') {
        // Strategy Planning Mode (Jan 2026) - Unified Deep + Comprehensive
        const assistantMessage: ChatMessage = {
          id: `msg_${Date.now() + 1}`,
          role: 'assistant',
          content: planningPath === 'express'
            ? `Let's build a growth strategy. I'll run a full analysis automatically — this takes about two minutes.`
            : planningPath === 'guided'
            ? `Let's build a strategy together. I'll walk you through three steps:\n\n1. First, I'll show you what I plan to analyze\n2. Then, how thoroughly I'll cover it\n3. Finally, the strategic framework I'd recommend\n\nYou approve each step before we move on.`
            : `Let me explore your business data and see what stands out. This is read-only — I won't make any recommendations yet.`,
          timestamp: new Date().toISOString(),
        }

        dispatch(addMessageToConversation({
          conversationId: activeConversationId,
          message: assistantMessage,
        }))

        try {
          // Show progress modal
          setProgressModal({ visible: true, mode: 'Strategy Planning', completed: false })

          // Phase 6 split: Express now returns a 202 handshake first, then we poll
          // the progress endpoint until the async job completes. Guided + Explore
          // continue to return their full response synchronously from /strategy/start.
          const handshake = await omapApi.startStrategy(
            userMessage.content,
            activeConversationId,
            planningPath,
            companyId
          )

          // Refusal short-circuit: scope classifier blocked the prompt (Plan 2D-a).
          // Refusal happens BEFORE the async split, so it can be returned by either
          // path. Hide progress modal, render refusal in chat, reset to Quick mode.
          // Two-signal check: /strategy/start may return status:'refused' (strategy state
          // machine) or a QueryResponse-shaped refusal (scope classifier passthrough).
          // Do not simplify to a single signal — they cover different backend paths.
          if (
            (handshake as any).status === 'refused' ||
            isRefusedResponse(handshake as any)
          ) {
            setProgressModal({ visible: false, mode: 'Strategy Planning', completed: false })
            const refusalMessage: ChatMessage = {
              id: `msg_${Date.now() + 1}`,
              role: 'assistant',
              content: (handshake as any).response || REFUSAL_FALLBACK_MESSAGE,
              timestamp: new Date().toISOString(),
            }
            dispatch(addMessageToConversation({
              conversationId: activeConversationId,
              message: refusalMessage,
            }))
            setSelectedMode('quick')
            return
          }

          // Phase 6: Express handshake (status='pending') -> poll until terminal.
          // Guided + Explore paths still return the full result on the first call.
          let workflowResponse: any
          if (planningPath === 'express' && (handshake as any).status === 'pending') {
            try {
              workflowResponse = await omapApi.pollStrategyJob(
                (handshake as any).conversation_id
              )
            } catch (err: any) {
              const status = (err as any)?.status ?? (err as any)?.response?.status
              if (status === 429) {
                const retryAfter = Number(
                  (err as any)?.response?.headers?.['retry-after'] ??
                  (err as any)?.retryAfter ?? 0,
                )
                showRateLimitToast(retryAfter || undefined)
                setProgressModal({ visible: false, mode: 'Strategy Planning', completed: false })
                setSelectedMode('quick')
                return
              }
              if ((err as any)?.name === 'PollingNetworkError') {
                showErrorToast(
                  'We lost the connection while your analysis was running. It may still be finishing, so please check back in a moment before retrying.',
                )
                setProgressModal({ visible: false, mode: 'Strategy Planning', completed: false })
                setSelectedMode('quick')
                return
              }
              setProgressModal({ visible: false, mode: 'Strategy Planning', completed: false })
              const errorMsg: ChatMessage = {
                id: `msg_${Date.now() + 1}`,
                role: 'assistant',
                content: `**The automatic analysis didn't finish**\n\nThis sometimes happens if the request was very broad or the service was briefly busy. You can:\n\n- **Try again** (most retries succeed).\n- Switch to **Guided** mode to build the strategy step by step.\n\n_(Technical detail: ${err?.errorCode || err?.message || 'no error code returned'})_`,
                timestamp: new Date().toISOString(),
              }
              dispatch(addMessageToConversation({
                conversationId: activeConversationId,
                message: errorMsg,
              }))
              setSelectedMode('quick')
              return
            }
          } else {
            // Guided + Explore: handshake already contains the full response payload
            workflowResponse = handshake
          }

          console.log('[Strategy Planning] Response:', workflowResponse)

          // Handle Express path error - show error message instead of Gate 1.
          // Must close the progress modal here: status:'error' is a successful HTTP
          // response carrying an error payload, so the outer catch won't fire and
          // the modal would otherwise hang at 95%.
          if (planningPath === 'express' && workflowResponse.status === 'error') {
            setProgressModal({ visible: false, mode: 'Strategy Planning', completed: false })
            const errorMsg: ChatMessage = {
              id: `msg_${Date.now() + 1}`,
              role: 'assistant',
              content: `**The automatic analysis didn't finish**\n\nThis sometimes happens if the request was very broad or the service was briefly busy. You can:\n\n- **Try again** (most retries succeed).\n- Switch to **Guided** mode to build the strategy step by step.\n\n_(Technical detail: ${workflowResponse.error || 'no error code returned'})_`,
              timestamp: new Date().toISOString(),
            }
            dispatch(addMessageToConversation({
              conversationId: activeConversationId,
              message: errorMsg,
            }))
            setSelectedMode('quick')
            return
          }

          // Handle Express path - returns complete result
          if (planningPath === 'express' && workflowResponse.status === 'complete') {
            // Update workspace with final artifacts
            dispatch(updateStrategyWorkspace({
              conversationId: activeConversationId,
              data: {
                meceTable: workflowResponse.mece_table || {},
                strategyMap: workflowResponse.strategy_map,
                comprehensiveGate: null,
              }
            }))

            // Update analysis report with Strategy Plan document
            const fullReport = workflowResponse.strategy_plan_document ||
              `# Strategy Planning Complete\n\nMECE Framework and Strategy Map are ready in the Strategy Workspace.`

            dispatch(updateAnalysisReport({
              conversationId: activeConversationId,
              data: {
                coverageAttestation: workflowResponse.coverage_attestation ||
                  workflowResponse.coverage_report?.coverage_attestation || '',
                strategicSynthesis: workflowResponse.strategic_synthesis || null,
                strategicObjectives: workflowResponse.strategic_objectives || [],
                fullReport,
              }
            }))

            // Update graph context if present
            if (workflowResponse.graph_context) {
              dispatch(setGraphContext(workflowResponse.graph_context))
            }

            // Build rich chat summary from synthesis data
            let expressContent = `**Strategy Planning (Express) Complete!**\n\n`
            const synthesis = workflowResponse.strategic_synthesis
            if (synthesis?.causal_narrative) {
              expressContent += `${synthesis.causal_narrative}\n\n`
            }
            const pillarDerivation = synthesis?.pillar_derivation as Array<{
              pillar_name?: string; graph_confidence?: number; tactic_count?: number
            }> | undefined
            if (pillarDerivation && pillarDerivation.length > 0) {
              expressContent += `**Strategic Pillars Identified:**\n`
              for (let i = 0; i < pillarDerivation.length; i++) {
                const p = pillarDerivation[i]
                const name = p.pillar_name || `Pillar ${i + 1}`
                const conf = p.graph_confidence != null ? `${p.graph_confidence}%` : 'N/A'
                const count = p.tactic_count || 0
                expressContent += `${i + 1}. ${name} (${conf} graph confidence, ${count} tactics)\n`
              }
              expressContent += `\n`
            }
            const methodology = synthesis?.methodology_summary as {
              nodes_explored?: number; nodes_approved?: number; overall_confidence?: number
            } | undefined
            const coverage = workflowResponse.coverage_report as {
              confidence_score?: number; total_explored?: number; total_approved?: number
            } | undefined
            const confScore = coverage?.confidence_score ?? methodology?.overall_confidence ?? 0
            const explored = methodology?.nodes_explored ?? coverage?.total_explored ?? 0
            const approved = methodology?.nodes_approved ?? coverage?.total_approved ?? 0
            if (confScore || explored) {
              expressContent += `**Coverage:** ${Math.round(confScore)}% confidence | ${explored} nodes explored | ${approved} approved\n\n`
            }
            expressContent += `View full details in the **Strategy Workspace** tabs.`

            const completionMsg: ChatMessage = {
              id: `msg_${Date.now() + 2}`,
              role: 'assistant',
              content: expressContent,
              timestamp: new Date().toISOString(),
            }

            dispatch(addMessageToConversation({
              conversationId: activeConversationId,
              message: completionMsg,
            }))

            // Reset to Quick mode after Express completion
            setSelectedMode('quick')
          } else {
            // Guided or Explore path - show Gate 1
            const workflowId = String(workflowResponse.conversation_id)
            const currentGate = workflowResponse.current_gate || 1

            // Update workflow state
            dispatch(updateWorkflowState({
              conversationId: activeConversationId,
              workflowState: {
                workflow_id: workflowId,
                current_gate: currentGate,
                planning_path: planningPath,
              }
            }))

            // Update workspace with Gate 1
            dispatch(updateStrategyWorkspace({
              conversationId: activeConversationId,
              data: {
                comprehensiveGate: {
                  current: 1,
                  title: 'DDCA Scope Definition',
                  data: {
                    user_query: userMessage.content,
                    constants: {
                      primary_axiom: workflowResponse.ddca_result?.primary_axiom || '',
                      query_intent: workflowResponse.query_intent || 'STRATEGIC',
                    },
                    variables: {
                      exhaustiveness_score: workflowResponse.ddca_result?.exhaustiveness_score || 0,
                      terms_count: workflowResponse.ddca_result?.terms?.length || 0,
                    },
                    framework: {
                      deductive_scope: workflowResponse.ddca_result?.deductive_scope || {},
                      terms: workflowResponse.ddca_result?.terms || [],
                    }
                  },
                },
                planningPath: planningPath,
              }
            }))

            // Gate ready message
            const gateMessage: ChatMessage = {
              id: `msg_${Date.now() + 2}`,
              role: 'assistant',
              content: `I've mapped out the scope of what I'd like to analyze. Take a look in the **[Strategy Workspace](#strategy-workspace)** and let me know if it looks right.`,
              timestamp: new Date().toISOString(),
            }

            dispatch(addMessageToConversation({
              conversationId: activeConversationId,
              message: gateMessage,
            }))
          }

          // Signal completion
          setProgressModal({ visible: true, mode: 'Strategy Planning', completed: true })
          setTimeout(() => setProgressModal({ visible: false, mode: 'Strategy Planning', completed: false }), 500)

        } catch (error: any) {
          console.error('[Strategy Planning] Error:', error)

          // Hide modal on error
          setProgressModal({ visible: false, mode: 'Strategy Planning', completed: false })

          const errorMsg: ChatMessage = {
            id: `msg_${Date.now() + 2}`,
            role: 'assistant',
            content: `❌ **Error:** ${error.message || 'Failed to start Strategy Planning'}\n\nCheck browser console (F12) for details.`,
            timestamp: new Date().toISOString(),
          }

          dispatch(addMessageToConversation({
            conversationId: activeConversationId,
            message: errorMsg,
          }))
        }
      }

    } catch (error: any) {
      const errorMessage: ChatMessage = {
        id: `msg_${Date.now() + 1}`,
        role: 'assistant',
        content: `❌ Error: ${error.message || 'Failed to process request'}`,
        timestamp: new Date().toISOString(),
      }

      dispatch(addMessageToConversation({
        conversationId: activeConversationId,
        message: errorMessage,
      }))
    } finally {
      setLoading(false)
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  }

  const lastProcessedTrigger = useRef<number>(0)

  const handleGateAdvancement = async (
    gate: 1 | 2,
    payload: any,
    modifications?: any
  ) => {
    if (!activeConversationId || loading) return

    setLoading(true)

    try {
      const workflowState = activeConversation?.workflowState
      if (!workflowState?.workflow_id) {
        throw new Error('No active workflow')
      }

      // Processing message
      const processingMsg: ChatMessage = {
        id: `msg_${Date.now()}`,
        role: 'assistant',
        content: `🔄 Processing Gate ${gate}...\n\nGenerating Gate ${gate + 1}...`,
        timestamp: new Date().toISOString(),
      }

      dispatch(addMessageToConversation({
        conversationId: activeConversationId,
        message: processingMsg,
      }))

      // Show progress modal
      setProgressModal({ visible: true, mode: 'Strategy Planning', completed: false })

      // Call Strategy Planning API (unified mode - Jan 2026)
      // Phase 1.1: Include selected_pillars for Gate 3
      const workflowResponse = await omapApi.advanceStrategy(
        workflowState.workflow_id,
        {
          approved: true,
          feedback: payload.feedback || '',
          modifications: modifications || {},
          selected_pillars: payload.selected_pillars,  // Phase 1.1: Pass selected pillars
        }
      )

      // Log response structure (not full content to avoid huge console dumps)
      console.log('[Gate Advancement] Backend response keys:', Object.keys(workflowResponse))
      console.log('[Gate Advancement] Has status?', !!(workflowResponse as any).status)
      console.log('[Gate Advancement] Has coverage_report?', !!(workflowResponse as any).coverage_report)
      console.log('[Gate Advancement] Has mece_table?', !!(workflowResponse as any).mece_table)

      // Determine next gate from backend response
      let nextGate: number | 'completed'

      // Check if response is wrapped in a data field
      const responseData = (workflowResponse as any).data || workflowResponse

      // Priority 1: Use explicit status field if available (new backend structure)
      if (responseData.status) {
        // Phase 1.2: Normalize status to lowercase for case-insensitive matching
        const normalizedStatus = responseData.status.toLowerCase()
        console.log('[Gate Advancement] Using status field:', responseData.status, '-> normalized:', normalizedStatus)

        // Backend error payload (e.g. comprehensive.py coverage failure). Surface the
        // backend's error text verbatim — don't let it fall through to the generic
        // "Unknown backend status" handler, which hides the root cause from the user.
        if (normalizedStatus === 'error') {
          setProgressModal({ visible: false, mode: 'Strategy Planning', completed: false })
          const errorMsg: ChatMessage = {
            id: `msg_${Date.now() + 1}`,
            role: 'assistant',
            content: `❌ **Strategy Planning Failed**\n\n${responseData.error || 'An unexpected error occurred during gate advancement.'}`,
            timestamp: new Date().toISOString(),
          }
          dispatch(addMessageToConversation({
            conversationId: activeConversationId,
            message: errorMsg,
          }))
          return
        }

        const statusToGateMap: { [key: string]: number | 'completed' } = {
          'awaiting_scope_approval': 1,       // Gate 1: DDCA Scope (initial state)
          'awaiting_ddca_approval': 1,        // Alias for Gate 1
          'awaiting_coverage_approval': 2,    // Gate 2: Coverage Report
          'awaiting_mece_approval': 3,        // Gate 3: MECE Table
          'complete': 'completed',
          'completed': 'completed',
        }
        nextGate = statusToGateMap[normalizedStatus]

        if (!nextGate) {
          console.error('[Gate Advancement] Unknown backend status:', responseData.status, '(normalized:', normalizedStatus, ')')
          console.error('[Gate Advancement] Response keys:', Object.keys(responseData))
          throw new Error(`Unknown backend status: ${responseData.status}`)
        }
      } else {
        // Priority 2: Fallback to property-based detection (old backend structure)
        console.log('[Gate Advancement] No status field, using property-based detection')
        if (responseData.approved_nodes && responseData.taxonomy_distribution) {
          // Response contains coverage data -> Gate 2
          console.log('[Gate Advancement] Detected Gate 2 data')
          nextGate = 2
        } else if (responseData.mece_table) {
          // Response contains MECE table -> Gate 3
          console.log('[Gate Advancement] Detected Gate 3 data')
          nextGate = 3
        } else if (responseData.strategy_map || responseData.complete) {
          // Response contains final artifacts -> Completed
          console.log('[Gate Advancement] Detected completion data')
          nextGate = 'completed'
        } else {
          console.error('[Gate Advancement] Could not determine gate. Response structure:', responseData)
          throw new Error(`Unable to determine next gate. Available keys: ${Object.keys(responseData).join(', ')}`)
        }
      }

      // Handle completion
      if (nextGate === 'completed') {
        handleWorkflowCompletion(responseData)

        // Signal completion
        setProgressModal({ visible: true, mode: 'Strategy Planning', completed: true })
        setTimeout(() => setProgressModal({ visible: false, mode: 'Strategy Planning', completed: false }), 500)

        return
      }

      // Update workflow state
      dispatch(updateWorkflowState({
        conversationId: activeConversationId,
        workflowState: {
          ...workflowState,
          current_gate: nextGate as number,
        }
      }))

      // Determine gate data
      let gateData: any = null
      let gateTitle: string = ''

      if (nextGate === 2) {
        // Handle both nested structure (new backend) and flat structure (old backend)
        // New: {status, coverage_report: {approved_nodes, ...}, top_approved_nodes}
        // Old: {approved_nodes, taxonomy_distribution, ...}
        console.log('[Gate Advancement] Mapping Gate 2 data...')
        console.log('[Gate Advancement] Response structure check:', {
          hasStatus: !!responseData.status,
          hasCoverageReport: !!responseData.coverage_report,
          hasTopApprovedNodes: !!responseData.top_approved_nodes,
          responseKeys: Object.keys(responseData)
        })

        const coverageData = responseData.coverage_report || responseData
        const approvedNodes = responseData.top_approved_nodes || coverageData.approved_nodes || []

        console.log('[Gate Advancement] Coverage data source:', responseData.coverage_report ? 'nested' : 'flat')
        console.log('[Gate Advancement] Approved nodes count:', approvedNodes.length)
        console.log('[Gate Advancement] Taxonomy keys:', Object.keys(coverageData.taxonomy_distribution || {}))
        console.log('[Gate Advancement] BSC keys:', Object.keys(coverageData.bsc_distribution || {}))
        console.log('[Gate Advancement] Coverage statement length:', (coverageData.coverage_statement || '').length)

        // Defensive: Ensure approved nodes have required fields for rendering
        const normalizedFactors = approvedNodes.map((node: any, idx: number) => ({
          factor_id: node.factor_id || node.id || node.node_id || `factor_${idx}`,
          factor_name: node.factor_name || node.name || node.description || 'Unnamed Factor',
          taxonomy: node.taxonomy || node.labels?.[0] || 'Unknown',
          perspective: node.perspective || node.bsc_perspective || 'Unknown',
          relevance_score: node.relevance_score ?? node.score ?? 5,
          reasoning: node.reasoning || node.rationale || 'No rationale provided',
          node_id: node.node_id || node.id || `node_${idx}`,
          name: node.name || node.factor_name || 'Unnamed',
        }))

        console.log('[Gate Advancement] Normalized factors count:', normalizedFactors.length)
        if (normalizedFactors.length > 0) {
          console.log('[Gate Advancement] First factor sample:', normalizedFactors[0])
        }

        gateData = {
          coverage_distribution: {
            taxonomies: coverageData.taxonomy_distribution || {},
            bsc_perspectives: coverageData.bsc_distribution || {}
          },
          proposed_factors: normalizedFactors,
          coverage_attestation: coverageData.coverage_statement || 'Coverage analysis complete.',
          confidence_score: coverageData.confidence_score || 0,
          // Include adaptive reasoning metadata if present (for UI display)
          adaptive_reasoning: coverageData.adaptive_reasoning || null,
          // Include BSRE analysis if present (optional, feature-gated)
          bsre_analysis: coverageData.bsre_analysis || null,
          // Include graph_context for Business Graph Tab (Jan 2026)
          graph_context: responseData.graph_context || null,
        }
        gateTitle = 'Coverage Report Validation'
      } else if (nextGate === 3) {
        // Backend returns: {mece_table: {...}, summary: {...}}
        // Frontend needs both mece_table and strategy_map
        // Note: Backend doesn't return strategy_map until Gate 3 approval (completion)
        // For display, we just show the MECE table at this gate
        gateData = {
          mece_table: responseData.mece_table || {},
          strategy_map: null,  // Will be generated after Gate 3 approval
          // Include graph_context for Business Graph Tab (Jan 2026)
          graph_context: responseData.graph_context || null,
        }
        gateTitle = 'MECE Framework Review'
      }

      if (!gateData || !gateTitle) {
        throw new Error('Invalid gate progression - missing gate data')
      }

      // Update workspace
      console.log('[Gate Advancement] Updating workspace with gate data...')
      dispatch(updateStrategyWorkspace({
        conversationId: activeConversationId,
        data: {
          comprehensiveGate: {
            current: nextGate,
            title: gateTitle,
            data: gateData,
          },
          // Include graph_context at top level for Business Graph Tab (Jan 2026)
          graphContext: responseData.graph_context || null,
          ...(nextGate === 3 && {
            meceTable: responseData.mece_table,
            strategyMap: responseData.strategy_map,
          })
        }
      }))
      console.log('[Gate Advancement] Workspace updated successfully')

      // Success message
      const successMsg: ChatMessage = {
        id: `msg_${Date.now() + 1}`,
        role: 'assistant',
        content: nextGate === 2
          ? `The coverage analysis is ready for your review. Check the **[Strategy Workspace](#strategy-workspace)** to see how thoroughly we're covering each area.`
          : nextGate === 3
          ? `I've put together a strategic framework based on the analysis. Review it in the **[Strategy Workspace](#strategy-workspace)** — this is the part where your judgment matters most.`
          : `**${gateTitle}** is ready for your review in the **[Strategy Workspace](#strategy-workspace)**.`,
        timestamp: new Date().toISOString(),
      }

      dispatch(addMessageToConversation({
        conversationId: activeConversationId,
        message: successMsg,
      }))

      // Signal completion
      setProgressModal({ visible: true, mode: 'Strategy Planning', completed: true })
      setTimeout(() => setProgressModal({ visible: false, mode: 'Strategy Planning', completed: false }), 500)

    } catch (error: any) {
      // Hide modal on error
      setProgressModal({ visible: false, mode: 'Strategy Planning', completed: false })

      const errorMsg: ChatMessage = {
        id: `msg_${Date.now() + 1}`,
        role: 'assistant',
        content: `❌ **Error:** ${error.message || 'Failed to advance'}`,
        timestamp: new Date().toISOString(),
      }

      dispatch(addMessageToConversation({
        conversationId: activeConversationId,
        message: errorMsg,
      }))
    } finally {
      setLoading(false)
    }
  }

  const transformStrategyMap = (backendStrategyMap: any) => {
    // Transform backend structure: pillars[] -> perspectives[]
    // Backend: pillars[] where each pillar has bsc_perspective field
    // Frontend: perspectives[] where each perspective contains objectives[]
    if (!backendStrategyMap || !backendStrategyMap.pillars || backendStrategyMap.pillars.length === 0) {
      return null
    }

    console.log('[Strategy Map Transform] Input pillars:', backendStrategyMap.pillars.length)

    // Group pillars by BSC perspective
    const perspectivesMap = new Map<string, any[]>()

    for (const pillar of backendStrategyMap.pillars) {
      const perspectiveName = pillar.bsc_perspective || 'Uncategorized'

      if (!perspectivesMap.has(perspectiveName)) {
        perspectivesMap.set(perspectiveName, [])
      }

      // Transform pillar to objective format expected by frontend
      const objective = {
        objective_id: pillar.objective_id,
        objective_name: pillar.name,
        description: pillar.description || '',
      }

      perspectivesMap.get(perspectiveName)!.push(objective)
    }

    // Convert map to perspectives array
    const perspectives = Array.from(perspectivesMap.entries()).map(([name, objectives], idx) => ({
      perspective_id: `perspective_${idx}`,
      perspective_name: name,
      objectives,
    }))

    console.log('[Strategy Map Transform] Output perspectives:', perspectives.length)

    return {
      perspectives,
      causal_links: backendStrategyMap.causal_chains || [],
    }
  }

  const handleWorkflowCompletion = (workflow: any) => {
    // Backend completion response: {status: "complete", conversation_id, strategy_map, mece_table, coverage_attestation, summary}
    console.log('[Workflow Completion] Backend response:', workflow)

    const workflowId = String(workflow.conversation_id)

    // Transform strategy map from backend structure to frontend structure
    const transformedStrategyMap = transformStrategyMap(workflow.strategy_map)
    console.log('[Workflow Completion] Transformed strategy map:', transformedStrategyMap)

    // Update final state
    dispatch(updateWorkflowState({
      conversationId: activeConversationId!,
      workflowState: {
        workflow_id: workflowId,
        current_gate: 'completed',
      }
    }))

    // Update workspace with final artifacts
    dispatch(updateStrategyWorkspace({
      conversationId: activeConversationId!,
      data: {
        meceTable: workflow.mece_table || {},
        strategyMap: transformedStrategyMap,
        comprehensiveGate: null,
      }
    }))

    // Update analysis report with strategic synthesis (Phase 2 - Strategic Coherence)
    dispatch(updateAnalysisReport({
      conversationId: activeConversationId!,
      data: {
        coverageAttestation: workflow.coverage_attestation || '',
        strategicSynthesis: workflow.strategic_synthesis || null,
        fullReport: `# Comprehensive Analysis Complete\n\nMECE Framework and Strategy Map are ready in the Strategy Workspace.`,
      }
    }))

    // Completion message
    const completionMsg: ChatMessage = {
      id: `msg_${Date.now() + 1}`,
      role: 'assistant',
      content: `🎉 **Comprehensive Mode Completed!**\n\n✅ MECE Table available\n✅ Strategy Map available\n✅ Analysis Report updated\n\nView in **Strategy Workspace** tab.`,
      timestamp: new Date().toISOString(),
    }

    dispatch(addMessageToConversation({
      conversationId: activeConversationId!,
      message: completionMsg,
    }))

    // Reset to Quick mode after Guided/Explore completion
    setSelectedMode('quick')
  }

  // Watch for gate advancement triggers
  useEffect(() => {
    const trigger = activeConversation?.workflowState?.advancement_trigger
    if (trigger && trigger.timestamp > lastProcessedTrigger.current) {
      lastProcessedTrigger.current = trigger.timestamp
      handleGateAdvancement(trigger.gate, trigger.payload, trigger.modifications)
    }
  }, [activeConversation?.workflowState?.advancement_trigger])

  if (!activeConversationId) {
    return (
      <div className="chat-panel">
        <div className="chat-empty-state">
          <p>No active conversation</p>
          <p className="hint">Select or create a conversation to start</p>
        </div>
      </div>
    )
  }

  return (
    <div className="chat-panel">
      <div className="chat-header">
        <h3>Chat</h3>
        <span className="chat-mode-indicator">{selectedMode} mode</span>
      </div>

      <div className="chat-messages">
        {activeConversation?.messages.map((message) => (
          <div key={message.id} className={`chat-message ${message.role}`}>
            <div className="message-header">
              <span className="message-role">
                {message.role === 'user' ? 'You' : 'Advisor'}
              </span>
              <span className="message-time">{formatTimestamp(message.timestamp)}</span>
            </div>
            <div className="message-content">
              {message.role === 'assistant' ? (
                <MarkdownWithTabLinks
                  content={parseImageMarkers(message.content)}
                />
              ) : (
                message.content
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="chat-message assistant">
            <div className="message-header">
              <span className="message-role">🤖 Assistant</span>
            </div>
            <div className="message-content loading">
              <span className="loading-dots">●●●</span> Processing...
            </div>
          </div>
        )}
        {orientationMessage && orientationTier && !loading && (
          <div className="orientation-message">
            <div className="orientation-header">
              <span className="orientation-label">Where we left off</span>
              <button
                className={`orientation-refresh-btn ${refreshingOrientation ? 'loading' : ''}`}
                onClick={handleRefreshOrientation}
                disabled={refreshingOrientation}
                title="Refresh orientation"
              >
                ↻
              </button>
            </div>
            <p className="orientation-topic">{orientationMessage.topic}</p>
            {orientationTier >= 2 && (
              <p className="orientation-substance">{orientationMessage.substance}</p>
            )}
            {orientationTier >= 3 && (
              <p className="orientation-status">{orientationMessage.status}</p>
            )}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-area">
        <div className="mode-selector">
          <button
            className={`mode-btn ${selectedMode === 'quick' ? 'active' : ''}`}
            onClick={() => setSelectedMode('quick')}
            title="Quick Mode: Fast tactical answers (10-30s)"
          >
            Quick
          </button>
          <button
            className={`mode-btn ${selectedMode === 'strategy' ? 'active' : ''}`}
            onClick={() => setSelectedMode('strategy')}
            title="Strategy Planning: Comprehensive strategic analysis"
          >
            Strategy Planning
          </button>
        </div>

        {selectedMode === 'strategy' && (
          <div className="planning-path-selector">
            <label className={`path-option ${planningPath === 'express' ? 'active' : ''}`}>
              <input
                type="radio"
                name="planningPath"
                value="express"
                checked={planningPath === 'express'}
                onChange={() => setPlanningPath('express')}
              />
              <span className="path-label">Express</span>
              <span className="path-desc">Auto-approve (~5min)</span>
            </label>
            <label className={`path-option ${planningPath === 'guided' ? 'active' : ''}`}>
              <input
                type="radio"
                name="planningPath"
                value="guided"
                checked={planningPath === 'guided'}
                onChange={() => setPlanningPath('guided')}
              />
              <span className="path-label">Guided</span>
              <span className="path-desc">3 gates (~3-5min)</span>
            </label>
            <label className={`path-option ${planningPath === 'explore' ? 'active' : ''}`}>
              <input
                type="radio"
                name="planningPath"
                value="explore"
                checked={planningPath === 'explore'}
                onChange={() => setPlanningPath('explore')}
              />
              <span className="path-label">Explore</span>
              <span className="path-desc">Research only</span>
            </label>
          </div>
        )}

        <form onSubmit={handleSubmit} className="chat-input-form">
          <textarea
            className="chat-input"
            placeholder={isSampleCompany ? "Sample company — read only. Create your own company to ask questions." : "Ask a strategic question..."}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSubmit(e)
              }
            }}
            rows={3}
            disabled={loading || isSampleCompany}
          />
          <button
            type="submit"
            className="chat-submit-btn"
            disabled={!input.trim() || loading || isSampleCompany}
          >
            {loading ? '⏳' : '➤'} Send
          </button>
        </form>
      </div>

      <ProgressModal
        isOpen={progressModal.visible}
        mode={progressModal.mode}
        completed={progressModal.completed}
        onClose={() => setProgressModal({ ...progressModal, visible: false })}
      />
    </div>
  )
}

export default ChatPanel
